/* =============================================================================
 * ProfileEditModal — one editor for the whole recruiting profile.
 *
 * Replaces six independent section dialogs (Personal / Athletic / Performance /
 * Team / Academics / Awards) that each had to be opened and closed on its own.
 * This is a presentational shell: it owns the single overlay + a section tab bar
 * and renders the ACTIVE section's existing edit component in `embedded` mode
 * (see Modal's `inline` prop), so each section keeps its own form, validation
 * and Save — but the athlete moves between sections with a tab instead of a
 * close-find-reopen treadmill. All data shaping stays in PlayerProfile, which
 * passes the active section node as children.
 * ========================================================================== */
import React from 'react';
import Modal from './Modal';

export type ProfileSection =
  | 'personal'
  | 'athletic'
  | 'performance'
  | 'team'
  | 'academic'
  | 'awards';

export const PROFILE_SECTIONS: { key: ProfileSection; label: string }[] = [
  { key: 'personal', label: 'Personal' },
  { key: 'athletic', label: 'Athletic' },
  { key: 'performance', label: 'Performance' },
  { key: 'team', label: 'Team' },
  { key: 'academic', label: 'Academics' },
  { key: 'awards', label: 'Awards' },
];

interface ProfileEditModalProps {
  /** The open section, or null when the editor is closed. */
  activeSection: ProfileSection | null;
  /** Switch tabs to another section. */
  onSelectSection: (section: ProfileSection) => void;
  /** Close the whole editor (X / Cancel / backdrop / Escape). */
  onClose: () => void;
  /** The active section's editor, rendered in `embedded` mode. */
  children: React.ReactNode;
  /** Override the tab set (defaults to all six). */
  sections?: { key: ProfileSection; label: string }[];
}

const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  activeSection,
  onSelectSection,
  onClose,
  children,
  sections = PROFILE_SECTIONS,
}) => {
  const isOpen = activeSection !== null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Edit profile"
      contentStyle={{
        background: 'white',
        borderRadius: '16px',
        maxWidth: '640px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'Manrope, sans-serif',
      }}
    >
      {/* Section tab bar — one destination, six views. */}
      <div
        role="tablist"
        aria-label="Profile sections"
        style={{
          display: 'flex',
          gap: '6px',
          padding: '12px 16px',
          borderBottom: '1px solid #E5E7EB',
          overflowX: 'auto',
          flexShrink: 0,
        }}
      >
        {sections.map((s) => {
          const active = s.key === activeSection;
          return (
            <button
              key={s.key}
              role="tab"
              aria-selected={active}
              onClick={() => { if (!active) onSelectSection(s.key); }}
              style={{
                flexShrink: 0,
                padding: '8px 14px',
                border: 'none',
                borderRadius: '8px',
                cursor: active ? 'default' : 'pointer',
                background: active ? '#F9550D' : 'transparent',
                color: active ? 'white' : '#6B7280',
                fontWeight: 700,
                fontSize: '13px',
                fontFamily: 'Manrope, sans-serif',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s',
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Active section — provides its own header, scroll body and Save/Cancel. */}
      {children}
    </Modal>
  );
};

export default ProfileEditModal;
